/*
 * Purpose: Ensure Mission START/GOAL anchors and runnable wiring on any workflow graph
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import type { Edge, Node, Workflow } from '@/stores/workflowStore';
import { createMissionGoalNode, createMissionStartNode } from '@/lib/missionBlocks';
import { missionGoalBlocks, missionStartBlockedReason } from '@/lib/missionRules';

const genId = () => Math.random().toString(36).slice(2, 11);

const X_START = 40;
const Y_ROW = 120;
const X_GAP = 220;

function operationalNodes(nodes: Node[]): Node[] {
  return nodes.filter(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action'
  );
}

function firstOperationalEntry(nodes: Node[], edges: Edge[], operational: Node[]): Node | undefined {
  if (operational.length === 0) return undefined;
  const withIncoming = new Set(edges.map((e) => e.to));
  return operational.find((n) => !withIncoming.has(n.id)) ?? operational[0];
}

/** Infer a human-readable GOAL outcome from flow name, actions, or hint. */
export function inferWorkflowOutcome(workflow: Workflow, hint?: string): string {
  const fromHint = hint?.trim();
  if (fromHint) {
    return fromHint.length > 120 ? `${fromHint.slice(0, 119)}…` : fromHint;
  }

  const existingGoal = missionGoalBlocks(workflow)[0];
  const existing = String(existingGoal?.config?.outcome ?? '').trim();
  if (existing) return existing;

  const description = String((workflow as Workflow & { description?: string }).description ?? '').trim();
  if (description) {
    return description.length > 120 ? `${description.slice(0, 119)}…` : description;
  }

  const lastAction = [...workflow.nodes].reverse().find((n) => n.type === 'action');
  const actionMsg = String(lastAction?.config?.message ?? lastAction?.config?.name ?? '').trim();
  if (actionMsg) {
    return actionMsg.length > 120 ? `${actionMsg.slice(0, 119)}…` : actionMsg;
  }

  const cleanName = workflow.name
    .replace(/\s*\(from template\)\s*$/i, '')
    .replace(/^[^\w]+\s*/, '')
    .trim();
  return cleanName || 'Run this flow successfully';
}

export interface EnsureMissionAnchorsOptions {
  outcome?: string;
  /** Inject manual trigger + log when no operational blocks exist (default true). */
  minimalRunnable?: boolean;
}

function insertGoalBetweenStartAndTargets(
  startId: string,
  goalId: string,
  edges: Edge[]
): Edge[] {
  const next = [...edges];
  const startOut = next.filter((e) => e.from === startId && e.to !== goalId);

  for (const edge of startOut) {
    const idx = next.findIndex((e) => e.id === edge.id);
    if (idx === -1) continue;
    next.splice(idx, 1);
    if (!next.some((e) => e.from === goalId && e.to === edge.to)) {
      next.unshift({ id: genId(), from: goalId, to: edge.to });
    }
  }

  if (!next.some((e) => e.from === startId && e.to === goalId)) {
    next.unshift({ id: genId(), from: startId, to: goalId });
  }

  return next;
}

function ensureEdge(edges: Edge[], from: string, to: string): Edge[] {
  if (edges.some((e) => e.from === from && e.to === to)) return edges;
  return [{ id: genId(), from, to }, ...edges];
}

/**
 * Idempotently adds START + GOAL and wires START → GOAL → first operational step.
 * Optionally seeds a minimal manual trigger + log chain when the graph is empty.
 */
export function ensureMissionAnchors(
  workflow: Workflow,
  options: EnsureMissionAnchorsOptions = {}
): Workflow {
  const { outcome: outcomeHint, minimalRunnable = true } = options;

  let nodes = workflow.nodes.map((n) => ({ ...n, config: { ...n.config } }));
  let edges = workflow.edges.map((e) => ({ ...e }));

  let operational = operationalNodes(nodes);

  if (minimalRunnable && operational.length === 0) {
    const outcome = inferWorkflowOutcome(workflow, outcomeHint);
    const trigger: Node = {
      id: genId(),
      type: 'trigger',
      subtype: 'manual',
      config: { name: 'Manual Trigger' },
      x: X_START + X_GAP * 2,
      y: Y_ROW,
    };
    const action: Node = {
      id: genId(),
      type: 'action',
      subtype: 'log',
      config: {
        name: 'Log Result',
        message: `${outcome} — completed`,
      },
      x: X_START + X_GAP * 3,
      y: Y_ROW,
    };
    nodes.push(trigger, action);
    edges.push({ id: genId(), from: trigger.id, to: action.id });
    operational = [trigger, action];
  }

  const outcome = inferWorkflowOutcome(workflow, outcomeHint);

  let startNode = nodes.find((n) => n.type === 'start');
  if (!startNode) {
    startNode = createMissionStartNode(X_START, Y_ROW - 80);
    nodes.unshift(startNode);
  } else {
    startNode = { ...startNode, x: startNode.x ?? X_START, y: startNode.y ?? Y_ROW - 80 };
    nodes = nodes.map((n) => (n.id === startNode!.id ? startNode! : n));
  }

  let goalNode = nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
  if (!goalNode) {
    goalNode = createMissionGoalNode(outcome, X_START + X_GAP, Y_ROW - 80);
    nodes.push(goalNode);
  } else {
    const currentOutcome = String(goalNode.config.outcome ?? '').trim();
    if (!currentOutcome) {
      goalNode = {
        ...goalNode,
        config: { ...goalNode.config, outcome, name: goalNode.config.name ?? 'Identified outcome' },
      };
      nodes = nodes.map((n) => (n.id === goalNode!.id ? goalNode! : n));
    }
  }

  edges = insertGoalBetweenStartAndTargets(startNode.id, goalNode.id, edges);

  const firstOp = firstOperationalEntry(nodes, edges, operational);
  if (firstOp) {
    edges = ensureEdge(edges, goalNode.id, firstOp.id);
  }

  const opIds = new Set(operational.map((n) => n.id));
  const minOpX = operational.length
    ? Math.min(...operational.map((n) => n.x))
    : X_START + X_GAP * 2;
  const targetMinX = X_START + X_GAP * 2;
  if (minOpX < targetMinX) {
    const shift = targetMinX - minOpX;
    nodes = nodes.map((n) => (opIds.has(n.id) ? { ...n, x: n.x + shift } : n));
  }

  return {
    ...workflow,
    nodes,
    edges,
    updatedAt: new Date(),
  };
}

/** True when anchors or wiring should be auto-repaired (e.g. on load or import). */
export function workflowNeedsMissionAnchors(workflow: Workflow): boolean {
  if (missionStartBlockedReason(workflow)) return true;
  if (missionGoalBlocks(workflow).length === 0) return true;
  const operational = operationalNodes(workflow.nodes);
  if (operational.length === 0) return true;
  return false;
}

/** Repair workflow if START/GOAL/runnable chain is incomplete. */
export function normalizeWorkflowMissionGraph(workflow: Workflow): Workflow {
  if (!workflowNeedsMissionAnchors(workflow)) return workflow;
  return ensureMissionAnchors(workflow);
}
